import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import toast from "react-hot-toast";
import { useLocation } from "react-router-dom";
import { fetchWithAuth } from "../../hooks/useAuth";

// Render only in development mode; supports Vite and fallback to NODE_ENV
const IS_DEV_ENV =
  (import.meta as any).env?.DEV ??
  (globalThis as any).process?.env?.NODE_ENV === "development";

function DevSidebar() {
  if (!IS_DEV_ENV) return null;
  const location = useLocation();
  const queryClient = useQueryClient();

  const orderId = React.useMemo(() => {
    const match = location.pathname.match(/^\/orders\/(\d+)(?:\/|$)/);
    return match ? Number(match[1]) : null;
  }, [location.pathname]);

  const webhookMutation = useMutation({
    mutationKey: ["simulate-netopia-webhook", orderId],
    mutationFn: async () => {
      if (!orderId) throw new Error("Order ID lipsă în URL");
      const response = await fetchWithAuth(
        `/api/admin/testing/netopia/webhook`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            orderId,
            status: "COMPLETED",
            amount: 100,
            currency: "RON",
          }),
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({} as any));
        throw new Error(
          (err && (err.message || err.error)) ||
            "Eroare la simularea webhook-ului Netopia"
        );
      }
      return response.json().catch(() => ({} as any));
    },
    onSuccess: async () => {
      toast.success("Webhook Netopia simulat");
      if (orderId) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["orders"] }),
          queryClient.invalidateQueries({
            queryKey: ["order", String(orderId)],
          }),
        ]);
      }
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Eroare la apelul webhook"
      );
    },
  });

  const isEnabled = Boolean(orderId);

  const clearCacheMutation = useMutation({
    mutationKey: ["admin-clear-cache"],
    mutationFn: async () => {
      const response = await fetchWithAuth(`/api/admin/cache/clear`, {
        method: "POST",
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({} as any));
        throw new Error(err?.message || "Eroare la curățarea cache-ului");
      }
      return response.json().catch(() => ({} as any));
    },
    onSuccess: async () => {
      toast.success("Cache server curățat");
      try {
        await queryClient.invalidateQueries();
      } catch {}
    },
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : "Eroare la curățarea cache-ului"
      );
    },
  });

  return (
    <div className="fixed bottom-6 right-6 z-50  flex-col gap-2 hidden">
      <button
        type="button"
        onClick={() => webhookMutation.mutate()}
        disabled={!isEnabled || webhookMutation.isPending}
        aria-label="Simulează Netopia webhook (COMPLETED)"
        className={`rounded-full text-white shadow-lg px-4 py-3 font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
          !isEnabled || webhookMutation.isPending
            ? "bg-gray-400 cursor-not-allowed"
            : "bg-blue-600 hover:bg-blue-700"
        }`}
        title={
          isEnabled
            ? "Trimite webhook Netopia pentru această comandă"
            : "Disponibil doar pe pagina detaliilor unei comenzi"
        }
      >
        {webhookMutation.isPending ? "Se trimite..." : "Netopia webhook"}
      </button>
      <button
        type="button"
        onClick={() => clearCacheMutation.mutate()}
        disabled={clearCacheMutation.isPending}
        aria-label="Curăță cache server"
        className={`rounded-full text-white shadow-lg px-4 py-3 font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 ${
          clearCacheMutation.isPending
            ? "bg-gray-400 cursor-not-allowed"
            : "bg-amber-600 hover:bg-amber-700"
        }`}
        title="Curăță cache-ul în memorie pe server"
      >
        {clearCacheMutation.isPending ? "Se curăță..." : "Curăță cache server"}
      </button>
    </div>
  );
}

export default DevSidebar;
